import React from 'react';

const NFAVisualization = ({ nfa }) => {
  if (!nfa || !nfa.states || nfa.states.length === 0) return null;

  // Generate textual representation of the automaton
  const generateNFADescription = () => {
    const lines = [];
    
    // General information
    lines.push(`Liczba stanów: ${nfa.states.length}`);
    lines.push(`Stan początkowy: q${nfa.startState.id}`);
    
    // Accepting states
    const acceptingStatesList = nfa.states
      .filter(state => state.isAccepting)
      .map(state => `q${state.id}`)
      .join(', ');
    
    lines.push(`Stany akceptujące: ${acceptingStatesList || "brak"}`);
    
    // Alphabet
    const alphabet = Array.from(nfa.alphabet).sort().join(', ');
    lines.push(`Alfabet: {${alphabet || "ε (tylko przejścia epsilon)"}}`);
    
    return lines;
  };

  // Generate visual representation of transitions
  const renderTransitions = () => {
    const transitionRows = [];
    
    // For each state
    for (const state of nfa.states) {
      // Symbol transitions
      for (const [symbol, targets] of state.transitions.entries()) {
        const targetIds = Array.from(targets)
          .map(target => `q${target.id}`)
          .join(', ');
        
        transitionRows.push(
          <tr key={`${state.id}-${symbol}`}>
            <td className={state.id === nfa.startState.id ? 'start-state' : ''}>
              q{state.id}
            </td>
            <td>{symbol}</td>
            <td>{`{${targetIds}}`}</td>
          </tr>
        );
      }
      
      // Epsilon transitions
      if (state.epsilonTransitions.size > 0) {
        const targetIds = Array.from(state.epsilonTransitions)
          .map(target => `q${target.id}`)
          .join(', ');
        
        transitionRows.push(
          <tr key={`${state.id}-epsilon`}>
            <td className={state.id === nfa.startState.id ? 'start-state' : ''}>
              q{state.id}
            </td>
            <td>ε</td>
            <td>{`{${targetIds}}`}</td>
          </tr>
        );
      }
    }
    
    return transitionRows;
  };

  // Generate visual representation of states
  const renderStates = () => {
    return (
      <div className="states-visualization">
        {nfa.states.map(state => (
          <div 
            key={state.id} 
            className={`state-node ${state.id === nfa.startState.id ? 'start-state' : ''} ${state.isAccepting ? 'accepting-state' : ''}`}
          >
            q{state.id}
          </div>
        ))}
      </div>
    );
  };

  const nfaDescription = generateNFADescription();

  return (
    <div className="nfa-visualization">
      <h3>Wygenerowany automat NFA:</h3>
      
      <div className="nfa-info">
        {nfaDescription.map((line, index) => (
          <div key={index} className="info-line">
            <span className="info-label">{line.split(':')[0]}:</span>
            <span className="info-value">{line.split(':')[1]}</span>
          </div>
        ))}
      </div>
      
      <div className="nfa-explanation">
        <h4>Wyjaśnienie funkcji przejścia w automacie NFA</h4>
        <p>
          Funkcja przejścia δ w niedeterministycznym automacie skończonym (NFA) określa, do jakich stanów automat
          może przejść po odczytaniu danego symbolu, będąc w określonym stanie.
        </p>
        <p>
          <strong>Cechy automatu NFA:</strong>
        </p>
        <ul>
          <li>Z jednego stanu, dla danego symbolu, może istnieć wiele możliwych przejść do różnych stanów</li>
          <li>Mogą istnieć przejścia epsilon (ε), które nie wymagają odczytania żadnego symbolu</li>
          <li>Mogą istnieć stany, z których nie ma żadnego przejścia dla danego symbolu</li>
        </ul>
        
        <p>
          <strong>Interpretacja zapisu δ(q, a) = {'{q₁, q₂, ...}'}:</strong>
        </p>
        <ul>
          <li>q - stan wyjściowy</li>
          <li>a - symbol z alfabetu (lub ε dla przejść epsilon)</li>
          <li>{'{q₁, q₂, ...}'} - zbiór stanów, do których można przejść</li>
        </ul>
        
        <div className="example">
          Przykład: δ(q0, a) = {'{q1, q2}'} oznacza, że będąc w stanie q0 i odczytując symbol 'a',
          automat może przejść do stanu q1 lub q2.
        </div>
        
        <p>
          Słowo jest akceptowane przez automat NFA, jeśli istnieje co najmniej jedna ścieżka przejść,
          która prowadzi od stanu początkowego do stanu akceptującego po odczytaniu całego słowa.
        </p>
      </div>
      
      <div className="nfa-states-section">
        <h4>Stany automatu:</h4>
        {renderStates()}
        <div className="state-legend">
          <div className="legend-item">
            <div className="state-node start-state mini"></div>
            <span>Stan początkowy</span>
          </div>
          <div className="legend-item">
            <div className="state-node accepting-state mini"></div>
            <span>Stan akceptujący</span>
          </div>
          <div className="legend-item">
            <div className="state-node mini"></div>
            <span>Stan normalny</span>
          </div>
        </div>
      </div>
      
      <div className="nfa-transitions-section">
        <h4>Funkcja przejścia:</h4>
        <div className="transitions-table-container">
          <table className="transitions-table">
            <thead>
              <tr>
                <th>Stan</th>
                <th>Symbol</th>
                <th>Przejście</th>
              </tr>
            </thead>
            <tbody>
              {renderTransitions()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NFAVisualization; 